import React, { useState, useRef, useEffect } from 'react';
import { IoSend, IoClose, IoChevronDown, IoFlash, IoSearchOutline, IoListOutline, IoMic, IoStop, IoGlobeOutline, IoBulbOutline } from 'react-icons/io5';
import { CText } from '../../../components/common';
import { useVoiceRecorder } from '../../../hooks/useVoiceRecorder';
import { speechToText } from '../../../api/sarvam';

export interface ChatOptions {
  deepSearch: boolean;
  multiAgent: boolean;
  topK: number;
  language: string;
  model: string;
}

export interface ModelOption {
  key: string;
  label: string;
  description?: string;
}

interface ChatInputProps {
  onSend: (message: string, options: ChatOptions) => void;
  isLoading: boolean;
  selectedCompanies: string[];
  onCompanySelect: (companies: string[]) => void;
  allCompanies: string[];
  colors: any;
  placeholder?: string;
  editText?: string | null;
  onEditCancel?: () => void;
  /** Whether deep search is allowed for the current subscription tier */
  deepSearchAllowed?: boolean;
  language: string;
  onLanguageChange: (lang: string) => void;
  /** Dynamic model options from API */
  modelOptions?: ModelOption[];
}

const LANGUAGE_OPTIONS: { code: string; label: string }[] = [
  { code: 'en-IN', label: 'English' },
  { code: 'hi-IN', label: 'Hindi' },
  { code: 'bn-IN', label: 'Bengali' },
  { code: 'gu-IN', label: 'Gujarati' },
  { code: 'kn-IN', label: 'Kannada' },
  { code: 'ml-IN', label: 'Malayalam' },
  { code: 'mr-IN', label: 'Marathi' },
  { code: 'ta-IN', label: 'Tamil' },
  { code: 'te-IN', label: 'Telugu' },
  { code: 'pa-IN', label: 'Punjabi' },
];

const DEFAULT_MODEL_OPTIONS: ModelOption[] = [
  { key: 'fast', label: 'Fast', description: 'Quick responses' },
];

const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  isLoading,
  selectedCompanies,
  onCompanySelect,
  allCompanies,
  colors,
  placeholder = 'Ask anything about stocks...',
  editText,
  onEditCancel,
  deepSearchAllowed = true,
  language,
  onLanguageChange,
  modelOptions,
}) => {
  const resolvedModelOptions = modelOptions && modelOptions.length > 0 ? modelOptions : DEFAULT_MODEL_OPTIONS;
  const [message, setMessage] = useState('');
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [companySearch, setCompanySearch] = useState('');
  const [deepSearch, setDeepSearch] = useState(false);
  const [multiAgent, setMultiAgent] = useState(true);
  const [topK, setTopK] = useState<number>(5);
  const [showTopKDropdown, setShowTopKDropdown] = useState(false);
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const [model, setModel] = useState<string>('fast');
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [sttLoading, setSttLoading] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const topKDropdownRef = useRef<HTMLDivElement>(null);
  const langDropdownRef = useRef<HTMLDivElement>(null);
  const modelDropdownRef = useRef<HTMLDivElement>(null);
  const { isRecording, startRecording, stopRecording } = useVoiceRecorder();

  const topKOptions = [3, 5, 10, 15, 20];

  // Pre-fill textarea when editing a message
  useEffect(() => {
    if (editText != null) {
      setMessage(editText);
      textareaRef.current?.focus();
    }
  }, [editText]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
    }
  }, [message]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowCompanyDropdown(false);
      }
      if (topKDropdownRef.current && !topKDropdownRef.current.contains(event.target as Node)) {
        setShowTopKDropdown(false);
      }
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target as Node)) {
        setShowLangDropdown(false);
      }
      if (modelDropdownRef.current && !modelDropdownRef.current.contains(event.target as Node)) {
        setShowModelDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSend = () => {
    if (message.trim()) {
      onSend(message.trim(), { deepSearch, multiAgent, topK, language, model });
      setMessage('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleMicClick = async () => {
    if (isRecording) {
      const blob = await stopRecording();
      if (blob) {
        setSttLoading(true);
        try {
          const result = await speechToText(blob);
          setMessage((prev) => (prev ? prev + ' ' : '') + result.text);
          // Auto-set language from detected language
          if (result.detected_language && result.detected_language !== 'en-IN') {
            onLanguageChange(result.detected_language);
          }
        } catch (err) {
          console.error('STT failed:', err);
        } finally {
          setSttLoading(false);
        }
      }
    } else {
      await startRecording();
    }
  };

  const filteredCompanies = allCompanies.filter(company =>
    company.toLowerCase().includes(companySearch.toLowerCase())
  );

  const toggleCompany = (company: string) => {
    if (selectedCompanies.includes(company)) {
      onCompanySelect(selectedCompanies.filter(c => c !== company));
    } else {
      onCompanySelect([...selectedCompanies, company]);
    }
  };

  const currentLangLabel = LANGUAGE_OPTIONS.find(l => l.code === language)?.label || 'English';

  return (
    <div
      className="p-4"
      style={{
        backgroundColor: colors.inputBackground,
        borderTop: `1px solid ${colors.borderColor}`,
      }}
    >
      {/* Selected Companies Tags */}
      {selectedCompanies.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {selectedCompanies.map(company => (
            <span
              key={company}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-sm"
              style={{
                backgroundColor: colors.primary + '20',
                color: colors.primary,
              }}
            >
              {company}
              <button
                onClick={() => toggleCompany(company)}
                className="hover:opacity-70"
              >
                {React.createElement(IoClose as any, { size: 14 })}
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Options Row */}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        {/* Company Selector */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setShowCompanyDropdown(!showCompanyDropdown)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg transition-colors"
            style={{
              backgroundColor: colors.backgroundColor,
              border: `1px solid ${colors.borderColor}`,
            }}
          >
            <CText type="r12" color={colors.grayScale500}>
              {selectedCompanies.length > 0
                ? `${selectedCompanies.length} selected`
                : 'All Companies'}
            </CText>
            {React.createElement(IoChevronDown as any, { size: 14, color: colors.grayScale500 })}
          </button>

          {/* Dropdown */}
          {showCompanyDropdown && (
            <div
              className="absolute bottom-full mb-2 left-0 w-64 max-h-64 overflow-hidden rounded-lg shadow-lg z-50"
              style={{
                backgroundColor: colors.backgroundColor,
                border: `1px solid ${colors.borderColor}`,
              }}
            >
              {/* Search */}
              <div className="p-2" style={{ borderBottom: `1px solid ${colors.borderColor}` }}>
                <div
                  className="flex items-center gap-2 px-2 py-1 rounded"
                  style={{ backgroundColor: colors.inputBackground }}
                >
                  {React.createElement(IoSearchOutline as any, { size: 14, color: colors.grayScale400 })}
                  <input
                    type="text"
                    value={companySearch}
                    onChange={(e) => setCompanySearch(e.target.value)}
                    placeholder="Search companies..."
                    className="flex-1 bg-transparent text-sm outline-none"
                    style={{ color: colors.textColor }}
                  />
                </div>
              </div>

              {/* Company List */}
              <div className="max-h-48 overflow-y-auto">
                {filteredCompanies.slice(0, 50).map(company => (
                  <div
                    key={company}
                    className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:opacity-80"
                    style={{
                      backgroundColor: selectedCompanies.includes(company)
                        ? colors.primary + '10'
                        : 'transparent',
                    }}
                    onClick={() => toggleCompany(company)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedCompanies.includes(company)}
                      onChange={() => {}}
                      style={{ accentColor: colors.primary }}
                    />
                    <CText type="r12" color={colors.textColor}>
                      {company}
                    </CText>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Toggle Options */}
        <button
          onClick={() => deepSearchAllowed && setDeepSearch(!deepSearch)}
          className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-colors ${!deepSearchAllowed ? 'opacity-50 cursor-not-allowed' : ''}`}
          style={{
            backgroundColor: deepSearch && deepSearchAllowed ? colors.primary + '20' : colors.backgroundColor,
            border: `1px solid ${deepSearch && deepSearchAllowed ? colors.primary : colors.borderColor}`,
          }}
          title={deepSearchAllowed ? 'Deep Search: Analyze full PDF documents' : 'Deep Search requires Pro plan'}
        >
          {React.createElement(IoSearchOutline as any, {
            size: 14,
            color: deepSearch && deepSearchAllowed ? colors.primary : colors.grayScale500,
          })}
          <CText type="r12" color={deepSearch && deepSearchAllowed ? colors.primary : colors.grayScale500}>
            Deep
          </CText>
          {!deepSearchAllowed && (
            <span
              className="text-[10px] font-bold px-1 rounded"
              style={{ backgroundColor: colors.primary, color: colors.white }}
            >
              PRO
            </span>
          )}
        </button>

        <button
          onClick={() => setMultiAgent(!multiAgent)}
          className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-colors`}
          style={{
            backgroundColor: multiAgent ? colors.primary + '20' : colors.backgroundColor,
            border: `1px solid ${multiAgent ? colors.primary : colors.borderColor}`,
          }}
          title="Multi-Agent: Use web search for latest data"
        >
          {React.createElement(IoFlash as any, {
            size: 14,
            color: multiAgent ? colors.primary : colors.grayScale500,
          })}
          <CText type="r12" color={multiAgent ? colors.primary : colors.grayScale500}>
            Multi-Agent
          </CText>
        </button>

        {/* Model Selector */}
        <div className="relative" ref={modelDropdownRef}>
          <button
            onClick={() => setShowModelDropdown(!showModelDropdown)}
            className="flex items-center gap-1 px-3 py-2 rounded-lg transition-colors"
            style={{
              backgroundColor: model === 'thinking' ? colors.primary + '20' : colors.backgroundColor,
              border: `1px solid ${model === 'thinking' ? colors.primary : colors.borderColor}`,
            }}
            title="Select AI model"
          >
            {React.createElement(model === 'thinking' ? IoBulbOutline as any : IoFlash as any, {
              size: 14,
              color: model === 'thinking' ? colors.primary : colors.grayScale500,
            })}
            <CText type="r12" color={model === 'thinking' ? colors.primary : colors.grayScale500}>
              {resolvedModelOptions.find(m => m.key === model)?.label || 'Fast'}
            </CText>
            {React.createElement(IoChevronDown as any, { size: 12, color: colors.grayScale500 })}
          </button>

          {showModelDropdown && (
            <div
              className="absolute bottom-full mb-2 left-0 w-40 rounded-lg shadow-lg z-50 overflow-hidden"
              style={{
                backgroundColor: colors.backgroundColor,
                border: `1px solid ${colors.borderColor}`,
              }}
            >
              {resolvedModelOptions.map(opt => (
                <div
                  key={opt.key}
                  className="px-3 py-2 cursor-pointer hover:opacity-80"
                  style={{
                    backgroundColor: model === opt.key ? colors.primary + '10' : 'transparent',
                  }}
                  onClick={() => {
                    setModel(opt.key);
                    setShowModelDropdown(false);
                  }}
                >
                  <CText
                    type="r12"
                    color={model === opt.key ? colors.primary : colors.textColor}
                  >
                    {opt.label}
                  </CText>
                  {opt.description && (
                    <div style={{ fontSize: 10, color: colors.grayScale400, marginTop: 1 }}>
                      {opt.description}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top K Selector */}
        <div className="relative" ref={topKDropdownRef}>
          <button
            onClick={() => setShowTopKDropdown(!showTopKDropdown)}
            className="flex items-center gap-1 px-3 py-2 rounded-lg transition-colors"
            style={{
              backgroundColor: colors.backgroundColor,
              border: `1px solid ${colors.borderColor}`,
            }}
            title="Number of results to retrieve"
          >
            {React.createElement(IoListOutline as any, {
              size: 14,
              color: colors.grayScale500,
            })}
            <CText type="r12" color={colors.grayScale500}>
              {topK} results
            </CText>
            {React.createElement(IoChevronDown as any, { size: 12, color: colors.grayScale500 })}
          </button>

          {/* TopK Dropdown */}
          {showTopKDropdown && (
            <div
              className="absolute bottom-full mb-2 left-0 w-32 rounded-lg shadow-lg z-50 overflow-hidden"
              style={{
                backgroundColor: colors.backgroundColor,
                border: `1px solid ${colors.borderColor}`,
              }}
            >
              {topKOptions.map(option => (
                <div
                  key={option}
                  className="px-3 py-2 cursor-pointer hover:opacity-80"
                  style={{
                    backgroundColor: topK === option ? colors.primary + '10' : 'transparent',
                  }}
                  onClick={() => {
                    setTopK(option);
                    setShowTopKDropdown(false);
                  }}
                >
                  <CText
                    type="r12"
                    color={topK === option ? colors.primary : colors.textColor}
                  >
                    {option} results
                  </CText>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Language Selector */}
        <div className="relative" ref={langDropdownRef}>
          <button
            onClick={() => setShowLangDropdown(!showLangDropdown)}
            className="flex items-center gap-1 px-3 py-2 rounded-lg transition-colors"
            style={{
              backgroundColor: language !== 'en-IN' ? colors.primary + '20' : colors.backgroundColor,
              border: `1px solid ${language !== 'en-IN' ? colors.primary : colors.borderColor}`,
            }}
            title="Response language"
          >
            {React.createElement(IoGlobeOutline as any, {
              size: 14,
              color: language !== 'en-IN' ? colors.primary : colors.grayScale500,
            })}
            <CText type="r12" color={language !== 'en-IN' ? colors.primary : colors.grayScale500}>
              {currentLangLabel}
            </CText>
            {React.createElement(IoChevronDown as any, { size: 12, color: colors.grayScale500 })}
          </button>

          {showLangDropdown && (
            <div
              className="absolute bottom-full mb-2 left-0 w-36 rounded-lg shadow-lg z-50 overflow-hidden max-h-52 overflow-y-auto"
              style={{
                backgroundColor: colors.backgroundColor,
                border: `1px solid ${colors.borderColor}`,
              }}
            >
              {LANGUAGE_OPTIONS.map(opt => (
                <div
                  key={opt.code}
                  className="px-3 py-2 cursor-pointer hover:opacity-80"
                  style={{
                    backgroundColor: language === opt.code ? colors.primary + '10' : 'transparent',
                  }}
                  onClick={() => {
                    onLanguageChange(opt.code);
                    setShowLangDropdown(false);
                  }}
                >
                  <CText
                    type="r12"
                    color={language === opt.code ? colors.primary : colors.textColor}
                  >
                    {opt.label}
                  </CText>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Input Row */}
      <div
        className="flex items-end gap-3 rounded-xl p-2"
        style={{
          backgroundColor: colors.backgroundColor,
          border: `1px solid ${isRecording ? colors.primary : colors.borderColor}`,
        }}
      >
        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={sttLoading ? 'Transcribing...' : placeholder}
          className="flex-1 resize-none outline-none text-sm"
          style={{
            backgroundColor: 'transparent',
            color: colors.textColor,
            minHeight: 24,
            maxHeight: 150,
          }}
          rows={1}
          disabled={sttLoading}
        />

        {editText != null && onEditCancel && (
          <button
            onClick={onEditCancel}
            className="p-2 rounded-lg transition-colors flex-shrink-0"
            style={{
              backgroundColor: colors.grayScale400,
            }}
            title="Cancel edit"
          >
            {React.createElement(IoClose as any, { size: 18, color: colors.white })}
          </button>
        )}

        {/* Mic button */}
        <button
          onClick={handleMicClick}
          disabled={sttLoading}
          className="p-2 rounded-lg transition-colors flex-shrink-0"
          style={{
            backgroundColor: isRecording ? '#ef4444' : colors.backgroundColor,
            border: `1px solid ${isRecording ? '#ef4444' : colors.borderColor}`,
            opacity: sttLoading ? 0.5 : 1,
          }}
          title={isRecording ? 'Stop recording' : 'Voice input'}
        >
          {isRecording
            ? React.createElement(IoStop as any, { size: 18, color: colors.white })
            : React.createElement(IoMic as any, { size: 18, color: colors.grayScale500 })
          }
        </button>

        <button
          onClick={handleSend}
          disabled={!message.trim()}
          className="p-2 rounded-lg transition-colors flex-shrink-0"
          style={{
            backgroundColor: message.trim() ? colors.primary : colors.grayScale400,
            opacity: message.trim() ? 1 : 0.5,
          }}
        >
          {React.createElement(IoSend as any, { size: 18, color: colors.white })}
        </button>
      </div>
    </div>
  );
};

export default ChatInput;
